import React, { ReactNode, isValidElement } from "react";
import Breadcrumb, { BreadcrumbParentType } from "./Breadcrumb";
import Markdown, { MarkdownToJSX } from "markdown-to-jsx";
import cn from "classnames";
import { MarkdownPageContext } from "@utils/MarkdownUtils";

const Markdown2JSX: MarkdownToJSX.Overrides = {
  GradientHeroTitle: ({ children }) => children,
  h1: ({ children }) => children,
};

export type PageHeaderType = {
  title: ReactNode | string;
  subtitle?: ReactNode | string;
  breadcrumbLabel: string;
  parents?: BreadcrumbParentType[];
  hubPath?: string;
  hubTitle?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  asideColumn?: ReactNode;
  capitalizeBreadcrumb?: boolean;
  children?: ReactNode;
  gridCols?: string;
  stickyBreadcrumb?: boolean;
  hasDivider?: boolean;
};

const PageHeader = ({
  title,
  subtitle,
  breadcrumbLabel,
  parents,
  hubPath,
  hubTitle,
  titleClassName,
  subtitleClassName,
  asideColumn,
  capitalizeBreadcrumb = true,
  children,
  gridCols,
  hasDivider = false,
  stickyBreadcrumb = false,
}: PageHeaderType) => {
  const Description = () => {
    if (!subtitle) return null;
    return (
      <p
        className={cn(
          "mb-0 max-w-5xl text-xl font-light text-gray-300",
          subtitleClassName
        )}
      >
        {/*
         * use when subtitle is type of JSX.Element. Example page types: blog post, help pages.
         * MarkdownPageContext is used to render the description when data comes from Strapi. Example page types: Hub, Pricing, Grump, etc.
         */}
        {isValidElement(subtitle) ? (
          subtitle
        ) : (
          <MarkdownPageContext options={{ forceInline: true }}>
            {subtitle}
          </MarkdownPageContext>
        )}
      </p>
    );
  };
  const Content = () => (
    <div className="grid grid-cols-1 gap-4">
      <h1
        id="title"
        className={cn(
          "mb-0 max-w-6xl text-4xl font-semibold text-accuRankerWhite",
          titleClassName
        )}
      >
        {typeof title === "string" ? (
          <Markdown
            options={{
              overrides: Markdown2JSX,
              enforceAtxHeadings: true,
              wrapper: React.Fragment,
            }}
          >
            {title}
          </Markdown>
        ) : (
          title
        )}
      </h1>
      <Description />
      {children}
    </div>
  );
  return (
    <>
      <Breadcrumb
        title={(breadcrumbLabel || "").replace(/<(.|\n)*?>/g, " ")}
        parents={parents}
        hubPath={hubPath}
        hubTitle={hubTitle}
        capitalizeTitle={capitalizeBreadcrumb}
        sticky={stickyBreadcrumb}
      />
      <div
        className={cn(
          "grid max-w-full grid-cols-1 items-start gap-14 pt-4 md:gap-12",
          // if asideColumn is used and grid columns are defined, use the grid columns value, otherwise, use the default layout.
          asideColumn ? gridCols || "md:grid-cols-[5fr_4fr]" : null,
          {
            "mb-14": !hasDivider,
            "mb-4": hasDivider,
          }
        )}
      >
        <Content />
        {asideColumn}
      </div>
    </>
  );
};

export default PageHeader;
